// src/components/ExecutionStages.tsx
// Minimal inline execution stages with checkmarks
// Shows progress through: Analyzing → Generating → Preparing → Executing

import React from 'react';

export interface ExecutionStage {
  id: string;
  label: string;
  status: 'pending' | 'in_progress' | 'completed';
}

interface Props {
  stages: ExecutionStage[];
  className?: string;
}

const ExecutionStages: React.FC<Props> = ({ stages, className = '' }) => {
  const getStatusIcon = (status: ExecutionStage['status']) => {
    switch (status) {
      case 'completed':
        return <span className="text-green-500">✓</span>;
      case 'in_progress':
        return (
          <span className="text-blue-500">
            <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </span>
        );
      case 'pending':
        return <span className="text-muted-foreground">○</span>;
    }
  };

  if (stages.length === 0) return null;

  return (
    <div className={`space-y-2 ${className}`}>
      {stages.map((stage, index) => (
        <div
          key={stage.id}
          className="flex items-center space-x-3 animate-fade-in"
          style={{ animationDelay: `${index * 50}ms` }}
        >
          <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
            {getStatusIcon(stage.status)}
          </div>
          <span
            className={`text-sm ${
              stage.status === 'in_progress'
                ? 'text-foreground font-medium'
                : stage.status === 'completed'
                ? 'text-muted-foreground'
                : 'text-muted-foreground/60'
            }`}
          >
            {stage.label}
          </span>
        </div>
      ))}
    </div>
  );
};

export default ExecutionStages;
